import { zodResolver } from '@hookform/resolvers/zod';
import { DateRangePicker } from '@optools/components/datetime/date-range-picker';
import { Button } from '@optools/components/ui/button';
import { ButtonGroup } from '@optools/components/ui/button-group';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { cn } from '@optools/components/ui/utils';
import { useForm } from 'react-hook-form';

import { formSchema, FormValues } from '../schemas';

export interface SearchFormProps {
  email?: string;
  fromDate?: string;
  loading: boolean;
  onSubmit: (values: FormValues) => void;
  toDate?: string;
  userId?: string;
}

export function SearchForm({
  email,
  fromDate,
  loading,
  onSubmit,
  toDate,
  userId,
}: SearchFormProps) {
  const dateRange = fromDate
    ? {
        from: new Date(fromDate),
        to: toDate ? new Date(toDate) : undefined,
      }
    : undefined;

  const form = useForm<FormValues>({
    defaultValues: { searchDates: dateRange, searchValue: email || userId },
    disabled: loading,
    resolver: zodResolver(formSchema),
  });

  return (
    <div className="flex items-start gap-2">
      <Form {...form}>
        <form className="flex items-start gap-2" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="searchDates"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date Range</FormLabel>
                <DateRangePicker
                  className={cn(!field.value && 'text-muted-foreground')}
                  dateRange={field.value}
                  placeholder="Select a Date Range"
                  onSelect={field.onChange}
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="searchValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Okta ID / Email</FormLabel>
                <ButtonGroup>
                  <FormControl>
                    <Input className="w-96" placeholder="Enter User ID or Email" {...field} />
                  </FormControl>
                  <Button
                    disabled={form.formState.disabled}
                    loading={loading}
                    type="submit"
                    variant="outline">
                    Search
                  </Button>
                </ButtonGroup>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
}
